import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Editor from "@monaco-editor/react";
import axiosInstance from "../../api/axiosInstance";

const USE_SPLIT_ENDPOINTS = false; // flip to true if you later add separate API endpoints

export default function LandingHtmlEditor() {
  const [html, setHtml] = useState("");
  const [css, setCss] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingHtml, setSavingHtml] = useState(false);
  const [savingCss, setSavingCss] = useState(false);
  const [stripScripts, setStripScripts] = useState(true);

  const updatedRef = useRef(null);   // Date
  const htmlEditorRef = useRef(null);
  const cssEditorRef = useRef(null);
  const focused = useRef("html");

  // --- load initial content ---
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axiosInstance.get("/cms/landing-html");
        if (!mounted) return;
        setHtml(data?.html ?? "");
        setCss(data?.css ?? "");
        updatedRef.current = data?.updatedAt ? new Date(data.updatedAt) : null;
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // --- small helper: sanitize HTML on save if desired ---
  const sanitizeHtml = useCallback((raw) => {
    if (!stripScripts) return raw;
    // remove <script>...</script> and inline on* handlers
    const withoutScripts = raw.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "");
    return withoutScripts.replace(/\son[a-z]+\s*=\s*"(?:[^"]*)"/gi, "");
  }, [stripScripts]);

  // --- save helpers ---
  const putCombined = useCallback(async (nextHtml, nextCss) => {
    await axiosInstance.put("/cms/landing-html", {
      html: nextHtml,
      css: nextCss,
      ifUnmodifiedSince: updatedRef.current ? updatedRef.current.toISOString() : null,
    });
  }, []);

  const saveHtml = useCallback(async () => {
    setSavingHtml(true);
    try {
      const nextHtml = sanitizeHtml(html);
      if (USE_SPLIT_ENDPOINTS) {
        await axiosInstance.put("/cms/landing-html/html", {
          html: nextHtml,
          ifUnmodifiedSince: updatedRef.current ? updatedRef.current.toISOString() : null,
        });
      } else {
        await putCombined(nextHtml, css);
      }
      updatedRef.current = new Date(); // optimistic
    } catch (e) {
      if (e?.response?.status === 409) alert("Changed by someone else. Reload.");
      else alert("Saving HTML failed.");
    } finally {
      setSavingHtml(false);
    }
  }, [html, css, putCombined, sanitizeHtml]);

  const saveCss = useCallback(async () => {
    setSavingCss(true);
    try {
      if (USE_SPLIT_ENDPOINTS) {
        await axiosInstance.put("/cms/landing-html/css", {
          css,
          ifUnmodifiedSince: updatedRef.current ? updatedRef.current.toISOString() : null,
        });
      } else {
        await putCombined(html, css);
      }
      updatedRef.current = new Date(); // optimistic
    } catch (e) {
      if (e?.response?.status === 409) alert("Changed by someone else. Reload.");
      else alert("Saving CSS failed.");
    } finally {
      setSavingCss(false);
    }
  }, [html, css, putCombined]);

  // --- format current editor (Monaco built-in formatter) ---
  const formatFocused = useCallback(() => {
    const ed = focused.current === "html" ? htmlEditorRef.current : cssEditorRef.current;
    ed?.getAction("editor.action.formatDocument")?.run();
  }, []);

  // --- monaco setup for each editor ---
  const commonEditorOptions = useMemo(() => ({
    wordWrap: "on",
    minimap: { enabled: false },
    fontSize: 14,
    lineNumbersMinChars: 3,
    automaticLayout: true,
    scrollBeyondLastLine: false,
    tabSize: 2,
    formatOnPaste: true,
    formatOnType: true,
    bracketPairColorization: { enabled: true },
    guides: { bracketPairs: true },
  }), []);

  const onHtmlMount = (editor, monaco) => {
    htmlEditorRef.current = editor;
    focused.current = "html";
    monaco?.languages?.html?.htmlDefaults?.setOptions?.({
      validate: true,
      format: { wrapLineLength: 100, contentUnformatted: "pre,code,textarea" },
    });
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => saveHtml());
    editor.onDidFocusEditorText(() => { focused.current = "html"; });
  };

  const onCssMount = (editor, monaco) => {
    cssEditorRef.current = editor;
    monaco?.languages?.css?.cssDefaults?.setOptions?.({
      validate: true,
      lint: { emptyRules: "warning", unknownAtRules: "warning", duplicateProperties: "warning" },
    });
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => saveCss());
    editor.onDidFocusEditorText(() => { focused.current = "css"; });
  };

  // per-pane overlay spinners
  const PaneOverlay = ({ label }) => (
    <div style={{
      position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(15,31,30,0.45)", color: "#fff", fontWeight: 700, gap: 10,
      zIndex: 3, backdropFilter: "blur(2px)"
    }}>
      <svg width="20" height="20" viewBox="0 0 50 50" aria-hidden="true">
        <g>
          <circle cx="25" cy="25" r="20" stroke="white" strokeWidth="5" fill="none" strokeDasharray="90 150" strokeLinecap="round">
            <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.8s" repeatCount="indefinite"/>
          </circle>
        </g>
      </svg>
      <span>{label}</span>
    </div>
  );

  if (loading) return <div style={{ padding: 16 }}>Loading…</div>;

  const year = new Date().getFullYear();

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, padding: 16 }}>
      {/* HTML side */}
      <div style={{ display: "grid", gridTemplateRows: "auto 1fr auto", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h3 style={{ margin: 0 }}>Landing HTML</h3>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
            <input
              type="checkbox"
              checked={stripScripts}
              onChange={(e) => setStripScripts(e.target.checked)}
            />
            Strip &lt;script&gt; tags on save
          </label>
        </div>

        <div style={{ position: "relative", borderRadius: 8, overflow: "hidden" }}>
          <Editor
            height="70vh"
            language="html"
            value={html}
            onChange={(v) => setHtml(v ?? "")}
            onMount={onHtmlMount}
            options={commonEditorOptions}
            loading={<div style={{ padding: 16 }}>Editor loading…</div>}
            theme="vs-dark"
          />
          {savingHtml && <PaneOverlay label="Saving HTML…" />}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn--soft" onClick={formatFocused}>Format</button>
          <button className="btn btn--primary" disabled={savingHtml} onClick={saveHtml}>
            {savingHtml ? "Saving…" : "Save HTML"}
          </button>
        </div>
      </div>

      {/* CSS side */}
      <div style={{ display: "grid", gridTemplateRows: "auto 1fr auto", gap: 8 }}>
        <h3 style={{ margin: 0 }}>Landing CSS (overrides)</h3>

        <div style={{ position: "relative", borderRadius: 8, overflow: "hidden" }}>
          <Editor
            height="70vh"
            language="css"
            value={css}
            onChange={(v) => setCss(v ?? "")}
            onMount={onCssMount}
            options={commonEditorOptions}
            loading={<div style={{ padding: 16 }}>Editor loading…</div>}
            theme="vs-dark"
          />
          {savingCss && <PaneOverlay label="Saving CSS…" />}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn--soft" onClick={formatFocused}>Format</button>
          <button className="btn btn--primary" disabled={savingCss} onClick={saveCss}>
            {savingCss ? "Saving…" : "Save CSS"}
          </button>
        </div>
      </div>

      {/* editor footer bar (UI credit, not injected into content) */}
      <div
        style={{
          gridColumn: "1 / -1",
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 12px",
          borderTop: "1px solid #e5e7eb",
          color: "#6B7280",
          fontSize: 12,
        }}
      >
        <span>
          {updatedRef.current
            ? `Last known update: ${updatedRef.current.toLocaleString()}`
            : "No update timestamp"}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 12, alignItems: "center" }}>
          <span>© {year} Lane Sands. All rights reserved.</span>
          <span>
             
          </span>
        </div>
      </div>
    </div>
  );
}
